/**
 * Study assignment rule service — facility auto-assignment configuration.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../utils/apiClient';

export interface StudyAssignmentRuleModality {
  id: number;
  name: string;
}

export type StudyAssignmentType = 'specific' | 'group' | 'least_loaded';

export interface StudyAssignmentRuleRadiologist {
  id: number;
  fullName: string;
  username?: string;
}

export interface StudyAssignmentRule {
  id: number;
  facilityId: number;
  name: string;
  modalityIds: number[];
  modalities: StudyAssignmentRuleModality[];
  matchSubspecialty: string | null;
  requiredExpertise: string | null;
  matchPriority: string | null;
  sortOrder: number;
  assignmentType: StudyAssignmentType;
  radiologistId: number | null;
  radiologistIds: number[];
  radiologist: StudyAssignmentRuleRadiologist | null;
  radiologists: StudyAssignmentRuleRadiologist[];
  onCallStartHour: number | null;
  onCallEndHour: number | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateStudyAssignmentRuleRequest {
  facilityId?: number;
  name: string;
  modalityIds?: number[];
  matchSubspecialty?: string | null;
  requiredExpertise?: string | null;
  matchPriority?: string | null;
  assignmentType?: StudyAssignmentType;
  radiologistId?: number | null;
  radiologistIds?: number[];
  onCallStartHour?: number | null;
  onCallEndHour?: number | null;
  isActive?: boolean;
}

export interface UpdateStudyAssignmentRuleRequest
  extends Partial<CreateStudyAssignmentRuleRequest> {}

interface ApiListResponse {
  success?: boolean;
  message?: string;
  data?: StudyAssignmentRule[];
  count?: number;
}

interface ApiSingleResponse {
  success?: boolean;
  message?: string;
  data?: StudyAssignmentRule;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.response && typeof error.response === 'object') {
      const response = error.response as { message?: string; error?: string };
      if (response.message) {
        return response.message;
      }
      if (response.error) {
        return response.error;
      }
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Study assignment rule request failed';
}

function toCreateApiPayload(payload: CreateStudyAssignmentRuleRequest) {
  return {
    facilityId: payload.facilityId,
    name: payload.name,
    modalityIds: payload.modalityIds,
    matchSubspecialty: payload.matchSubspecialty,
    requiredExpertise: payload.requiredExpertise,
    matchPriority: payload.matchPriority || null,
    assignmentType: payload.assignmentType ?? 'specific',
    radiologistId: payload.radiologistId ?? null,
    radiologistIds: payload.radiologistIds,
    onCallStartHour: payload.onCallStartHour ?? null,
    onCallEndHour: payload.onCallEndHour ?? null,
    isActive: payload.isActive,
  };
}

function toUpdateApiPayload(payload: UpdateStudyAssignmentRuleRequest) {
  const body: Record<string, unknown> = {};

  if (payload.facilityId !== undefined) {
    body.facilityId = payload.facilityId;
  }
  if (payload.name !== undefined) {
    body.name = payload.name;
  }
  if (payload.modalityIds !== undefined) {
    body.modalityIds = payload.modalityIds;
  }
  if (payload.matchSubspecialty !== undefined) {
    body.matchSubspecialty = payload.matchSubspecialty;
  }
  if (payload.requiredExpertise !== undefined) {
    body.requiredExpertise = payload.requiredExpertise;
  }
  if (payload.matchPriority !== undefined) {
    body.matchPriority = payload.matchPriority || null;
  }
  if (payload.assignmentType !== undefined) {
    body.assignmentType = payload.assignmentType;
  }
  if (payload.radiologistId !== undefined) {
    body.radiologistId = payload.radiologistId;
  }
  if (payload.radiologistIds !== undefined) {
    body.radiologistIds = payload.radiologistIds;
  }
  if (payload.onCallStartHour !== undefined) {
    body.onCallStartHour = payload.onCallStartHour;
  }
  if (payload.onCallEndHour !== undefined) {
    body.onCallEndHour = payload.onCallEndHour;
  }
  if (payload.isActive !== undefined) {
    body.isActive = payload.isActive;
  }

  return body;
}

export async function listStudyAssignmentRules(facilityId?: number): Promise<{
  success: boolean;
  data?: StudyAssignmentRule[];
  error?: string;
}> {
  try {
    const url = API_ENDPOINTS.STUDY_ASSIGNMENT_RULES.LIST(facilityId);
    const response = await apiGet<ApiListResponse>(url);
    const body = response.data;

    if (body?.success === false) {
      return { success: false, error: body.message || 'Failed to load assignment rules' };
    }

    if (Array.isArray(body?.data)) {
      return { success: true, data: body.data };
    }

    return { success: false, error: 'Invalid assignment rules response' };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function createStudyAssignmentRule(
  payload: CreateStudyAssignmentRuleRequest
): Promise<{ success: boolean; data?: StudyAssignmentRule; error?: string }> {
  try {
    const response = await apiPost<ApiSingleResponse>(
      API_ENDPOINTS.STUDY_ASSIGNMENT_RULES.CREATE,
      toCreateApiPayload(payload)
    );
    const body = response.data;

    if (body?.success === false || !body?.data) {
      return { success: false, error: body?.message || 'Failed to create assignment rule' };
    }

    return { success: true, data: body.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function updateStudyAssignmentRule(
  id: number,
  payload: UpdateStudyAssignmentRuleRequest
): Promise<{ success: boolean; data?: StudyAssignmentRule; error?: string }> {
  try {
    const response = await apiPut<ApiSingleResponse>(
      API_ENDPOINTS.STUDY_ASSIGNMENT_RULES.UPDATE(id),
      toUpdateApiPayload(payload)
    );
    const body = response.data;

    if (body?.success === false || !body?.data) {
      return { success: false, error: body?.message || 'Failed to update assignment rule' };
    }

    return { success: true, data: body.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function deleteStudyAssignmentRule(
  id: number,
  facilityId?: number
): Promise<{ success: boolean; error?: string }> {
  try {
    await apiDelete(API_ENDPOINTS.STUDY_ASSIGNMENT_RULES.DELETE(id, facilityId));
    return { success: true };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function reorderStudyAssignmentRules(
  ruleIds: number[],
  facilityId?: number
): Promise<{ success: boolean; data?: StudyAssignmentRule[]; error?: string }> {
  try {
    const response = await apiPut<ApiListResponse>(API_ENDPOINTS.STUDY_ASSIGNMENT_RULES.REORDER, {
      ruleIds,
      facilityId,
    });
    const body = response.data;

    if (body?.success === false) {
      return { success: false, error: body.message || 'Failed to reorder assignment rules' };
    }

    if (Array.isArray(body?.data)) {
      return { success: true, data: body.data };
    }

    return { success: false, error: 'Invalid reorder response' };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}
